import { formatDateLocal, formatTime24 } from "@/shared/lib/date-time";
import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Loader,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  toast,
} from "@devhop/ui";
import { useRouterBack } from "@/shared/hooks/useRouterBack";
import { ArrowLeft, Calculator, Clock, Eye, Printer } from "lucide-react";
import { useState } from "react";
import type { Shift } from "../../domain/repo/get-shifts";
import { posApi } from "../api/client";
import { useRecalculateShiftMutation, useShiftsList } from "../api/queries";
import { usePOSContext } from "../context/POSContext";
import { printShiftReport } from "../lib/print-shift-report";
import { ShiftDetailDialog } from "../ui/ShiftDetailDialog";

function formatShiftOpenedAt(shift: Shift): string {
  const d = shift.openedAt;
  const dateStr =
    d instanceof Date ? formatDateLocal(d) : formatDateLocal(String(d));
  const timeStr = d instanceof Date ? formatTime24(d) : formatTime24(String(d));
  return `${dateStr} ${timeStr}`;
}

function formatShiftClosedAt(shift: Shift): string {
  const d = shift.closedAt;
  if (d == null) return "—";
  const dateStr =
    d instanceof Date ? formatDateLocal(d) : formatDateLocal(String(d));
  const timeStr = d instanceof Date ? formatTime24(d) : formatTime24(String(d));
  return `${dateStr} ${timeStr}`;
}

const PAGE_SIZE = 20;

export function ShiftsPage() {
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;
  const goBack = useRouterBack();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useShiftsList(branchId, page, PAGE_SIZE);
  const [printingShiftId, setPrintingShiftId] = useState<string | null>(null);
  const [recalculatingShiftId, setRecalculatingShiftId] = useState<
    string | null
  >(null);
  const [detailShiftId, setDetailShiftId] = useState<string | null>(null);
  const recalculateMutation = useRecalculateShiftMutation();

  const shifts = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handlePrintReport = async (shiftId: string) => {
    if (!branchId) return;
    setPrintingShiftId(shiftId);
    try {
      const report = await posApi.getShiftReport(shiftId, branchId);
      printShiftReport(report);
      toast.success("ກຳລັງເປີດຫນ້າພິມລາຍງານກະ");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "ເກີດຂໍ້ຜິດພາດໃນການດຶງລາຍງານ",
      );
    } finally {
      setPrintingShiftId(null);
    }
  };

  const handleRecalculate = (shiftId: string) => {
    if (!branchId) return;
    setRecalculatingShiftId(shiftId);
    recalculateMutation.mutate(
      { shiftId, branchId },
      {
        onSuccess: () => {
          toast.success("ຄຳນວນເງິນຄາດວ່າໃໝ່ສຳເລັດ");
        },
        onError: (err) => {
          toast.error(
            err instanceof Error ? err.message : "ເກີດຂໍ້ຜິດພາດໃນການຄຳນວນໃໝ່",
          );
        },
        onSettled: () => {
          setRecalculatingShiftId(null);
        },
      },
    );
  };

  if (!branchId) {
    return (
      <div className="container mx-auto max-w-4xl p-4">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Clock className="h-8 w-8 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>ບໍ່ມີສາຂາ</EmptyTitle>
            <EmptyDescription>ກະລຸນາເລືອກສາຂາແລະເຄື່ອງ POS ກ່ອນ</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto max-h-[calc(100svh-10rem)] max-w-5xl overflow-y-auto p-4">
        <div className="mb-6 flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="font-bold text-2xl">ລາຍການກະ</h1>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <span className="inline-flex size-8 animate-spin text-muted-foreground">
              <Loader />
            </span>
          </div>
        ) : shifts.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Clock className="h-12 w-12 text-muted-foreground" />
              </EmptyMedia>
              <EmptyTitle>ຍັງບໍ່ມີກະ</EmptyTitle>
              <EmptyDescription>ບໍ່ມີລາຍການກະໃນສາຂານີ້</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <>
            <div className="space-y-3">
              {shifts.map((shift) => {
                const isClosed = shift.status === "CLOSED";
                const openingFloat = Number(shift.openingFloat);
                const closingAmount =
                  shift.closingAmount != null
                    ? Number(shift.closingAmount)
                    : null;
                const variance =
                  shift.varianceAmount != null
                    ? Number(shift.varianceAmount)
                    : null;

                return (
                  <div
                    key={shift.id}
                    className="flex flex-wrap items-center justify-between gap-4 rounded-lg border bg-card p-4"
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">
                          ເປີດກະ: {formatShiftOpenedAt(shift)}
                        </span>
                        <Badge
                          variant={isClosed ? "secondary" : "default"}
                          className="shrink-0"
                        >
                          {isClosed ? "ປິດກະແລ້ວ" : "ເປີດຢູ່"}
                        </Badge>
                      </div>
                      <div className="text-muted-foreground text-sm">
                        ປິດກະ: {formatShiftClosedAt(shift)}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span>
                          ເງິນຕົ້ນກະ: {openingFloat.toLocaleString("lo-LA")} ₭
                        </span>
                        {closingAmount != null && (
                          <span>
                            ເງິນນັບຈິງ: {closingAmount.toLocaleString("lo-LA")} ₭
                          </span>
                        )}
                        {variance != null && (
                          <span
                            className={
                              variance !== 0
                                ? variance > 0
                                  ? "text-green-700"
                                  : "text-destructive"
                                : ""
                            }
                          >
                            ສ່ວນຕ່າງ: {variance.toLocaleString("lo-LA")} ₭
                          </span>
                        )}
                      </div>
                      {shift.notes && (
                        <p className="text-muted-foreground text-xs">
                          ໝາຍເຫດ: {shift.notes}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDetailShiftId(shift.id)}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        ລາຍລະອຽດ
                      </Button>
                      {isClosed && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={recalculatingShiftId !== null}
                            onClick={() => handleRecalculate(shift.id)}
                          >
                            {recalculatingShiftId === shift.id ? (
                              <span className="mr-2 inline-block size-4 animate-spin">
                                <Loader />
                              </span>
                            ) : (
                              <Calculator className="mr-2 h-4 w-4" />
                            )}
                            ຄຳນວນໃໝ່
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={printingShiftId !== null}
                            onClick={() => handlePrintReport(shift.id)}
                          >
                            {printingShiftId === shift.id ? (
                              <span className="mr-2 inline-block size-4 animate-spin">
                                <Loader />
                              </span>
                            ) : (
                              <Printer className="mr-2 h-4 w-4" />
                            )}
                            ພິມລາຍງານ
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <span
                        className={
                          page === 1
                            ? "pointer-events-none opacity-50"
                            : "cursor-pointer"
                        }
                      >
                        <PaginationPrevious
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        />
                      </span>
                    </PaginationItem>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (pageNum) => {
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= page - 1 && pageNum <= page + 1)
                        ) {
                          return (
                            <PaginationItem key={pageNum}>
                              <span className="cursor-pointer">
                                <PaginationLink
                                  onClick={() => setPage(pageNum)}
                                  isActive={page === pageNum}
                                >
                                  {pageNum}
                                </PaginationLink>
                              </span>
                            </PaginationItem>
                          );
                        }
                        if (pageNum === page - 2 || pageNum === page + 2) {
                          return (
                            <PaginationItem key={pageNum}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          );
                        }
                        return null;
                      },
                    )}
                    <PaginationItem>
                      <span
                        className={
                          page === totalPages
                            ? "pointer-events-none opacity-50"
                            : "cursor-pointer"
                        }
                      >
                        <PaginationNext
                          onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                        />
                      </span>
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </div>
      <ShiftDetailDialog
        shiftId={detailShiftId}
        branchId={branchId}
        open={detailShiftId !== null}
        onOpenChange={(open) => !open && setDetailShiftId(null)}
      />
    </>
  );
}
